import { fetchData } from 'Utils/fetch-data';
import { BaseApi } from './base-api';
import type {
  CourseEmployeeI,
  CourseEmployeeFilterI,
  UserDevelopmentByI,
  UserI,
  UserFilterI,
  GetUserCoursesQuery,
  GetUserCoursesResponse,
  UsersQuery,
  Users,
} from 'types';

class EmployeesApi extends BaseApi<UserI, UserFilterI> {
  /**
   * Obtiene todos los empleados que pertenecen a un curso en específico.
   * Este método se utiliza para obtener a los empleados al momento de revisar la evaluación
   * manual de un curso.
   *
   * @see {@link src/Hooks/useCoursesLessonsPage}
   * @param courseId - ID del curso.
   * @param filter - Filtros para obtener los empleados.
   */
  async readEmployeesByCourseId(courseId: string, lessonId: string, filter: CourseEmployeeFilterI = {}) {
    const response = await fetchData<CourseEmployeeI[]>(
      `${this.endpoint}/courses/${courseId}/lessons/${lessonId}/users`,
      {
        method: 'GET',
        params: filter,
      }
    );
    return response;
  }

  /**
   * Obtiene el plan de desarrollo de un empleado. Es decir, los cursos
   * que tiene asignados en el sistema.
   * @param userId - ID del empleado.
   */
  async readDevelopmentPlan(userId: string) {
    const response = await fetchData<UserDevelopmentByI>(`${this.endpoint}/${userId}/development-plan`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * Reinicia el progreso de un curso de un empleado. Se utiliza en el la página de "Plan de desarrollo"
   * para reiniciar el progreso de un curso.
   *
   * @param userId - ID del empleado.
   * @param userCourseId - ID del curso.
   */
  async resetUserCourseProgress(userId: string, userCourseId: string) {
    const response = await fetchData<boolean>(
      `${this.endpoint}/${userId}/user-courses/${userCourseId}/actions/reset-progress`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  /**
   * `GET /users/:userId/courses?assigned=true|false&enrolled=false`
   *
   * Obtiene los cursos que tiene asignado el empleado, aunque puedes
   * específicar para obtener los que no tiene.
   *
   * @param {string} userId
   * @param {GetUserCoursesQuery} options
   */
  async getCourses(userId: string, { assigned = true, enrolled = false }) {
    const url = `${this.endpoint}/${userId}/courses`;
    const params: GetUserCoursesQuery = {};

    if (assigned !== undefined) {
      params.assigned = assigned;
    }
    if (enrolled !== undefined) {
      params.enrolled = enrolled;
    }

    const response = await fetchData<GetUserCoursesResponse[]>(url, {
      method: 'GET',
      params,
    });

    return response;
  }

  /**
   * Actualiza el estatus de un usuario.
   * @param userId - ID del usuario.
   * @param status - true para activar, false para inactivar.
   */
  async updateStatus(userId: string, status: boolean) {
    const response = await fetchData<{ id: string; status: boolean }>(`${this.endpoint}/${userId}`, {
      method: 'PATCH',
      data: { status },
    });
    return response;
  }

  /**
   * Obtiene a los empleados del sistema.
   */
  async getUsers(params: UsersQuery) {
    const url = `${this.endpoint}/`;
    const response = await fetchData<Users['data']>(url, {
      method: 'GET',
      params,
      includeJsonApiMeta: true,
    });
    return response;
  }
}

const employeesApi = new EmployeesApi('/users');

export default employeesApi;
